import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { playUrl } from '@/lib/wordAudio';
import { speakingApi, type SpeakingRoom, type AIAnalysis, type SpeakingHistoryEntry } from '@/api/speaking';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import { humanizeError } from '@/lib/errors';
import { aiSpeakingShown } from '@/lib/aiEntries';
import { useAppConfig } from '@/hooks/useAppConfig';

const TAB_IDS = ['rooms', 'ai', 'history'] as const;
type TabId = (typeof TAB_IDS)[number];

export function StudentSpeakingPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TabId>('rooms');

  // ⚠️ Вкладка разговора с ИИ спрятана (D22, 11.09.2026), сама страница — нет:
  // здесь же разговорные комнаты, живые занятия с людьми.
  useAppConfig();
  const aiShown = aiSpeakingShown();

  const TABS: { id: TabId; label: string }[] = [
    { id: 'rooms', label: t('speaking.tabRooms') },
    { id: 'ai', label: t('speaking.tabAI') },
    { id: 'history', label: t('speaking.tabHistory') },
  ].filter((tb) => tb.id !== 'ai' || aiShown) as { id: TabId; label: string }[];

  return (
    <>
      <PageHeader title={t('speaking.title')} subtitle={t('speaking.subtitle')} />
      <div className="mb-6 flex gap-2">
        {TABS.map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            className={cn(
              'rounded-pill px-4 py-1.5 text-xs font-semibold uppercase tracking-label transition',
              tab === tb.id
                ? 'bg-primary text-on-primary'
                : 'bg-surface-container-lowest text-on-surface-variant ghost-border hover:bg-surface-container-low',
            )}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {tab === 'rooms' && <PeerRoomsTab />}
      {tab === 'ai' && aiShown && <AITab />}
      {tab === 'history' && <HistoryTab />}
    </>
  );
}

function PeerRoomsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const rooms = useQuery({ queryKey: ['speaking', 'rooms'], queryFn: () => speakingApi.listRooms() });
  const book = useMutation({
    mutationFn: (id: string) => speakingApi.bookRoom(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['speaking', 'rooms'] }),
  });

  if (rooms.isLoading) return <Card variant="subtle">{t('common.loading')}</Card>;
  if (!rooms.data?.length) {
    return (
      <Card variant="subtle" className="text-center text-on-surface-variant">
        <span className="material-symbols-outlined mb-2 block text-4xl text-on-surface-variant/60">
          groups
        </span>
        {t('speaking.noRooms')}
      </Card>
    );
  }
  return (
    <>
      {book.isError && (
        <Card variant="subtle" className="mb-3 text-error">
          {humanizeError(book.error)}
        </Card>
      )}
      <ul className="grid gap-3 md:grid-cols-2">
        {rooms.data.map((r) => (
          <li key={r.id}>
            <RoomCard
              room={r}
              onBook={() => book.mutate(r.id)}
              booking={book.isPending && book.variables === r.id}
            />
          </li>
        ))}
      </ul>
    </>
  );
}

function RoomCard({ room, onBook, booking }: { room: SpeakingRoom; onBook: () => void; booking?: boolean }) {
  const { t } = useTranslation();
  const date = new Date(room.scheduled_at);
  const isFull = (room.current_bookings ?? 0) >= room.max_participants;
  return (
    <Card>
      <CardTitle>{room.title}</CardTitle>
      {room.topic && <p className="mt-1 text-sm text-on-surface-variant">{room.topic}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        {room.level && <Pill variant="primary">{room.level}</Pill>}
        {room.language && <Pill variant="neutral">{room.language}</Pill>}
        <Pill variant="warning">{t('speaking.durationMin', { min: room.duration_min })}</Pill>
      </div>
      <p className="mt-2 text-xs text-on-surface-variant">
        {date.toLocaleString('ru-RU', { dateStyle: 'medium', timeStyle: 'short' })} ·{' '}
        {t('speaking.participants', { current: room.current_bookings ?? 0, max: room.max_participants })}
      </p>
      <div className="mt-4 flex gap-2">
        <Button disabled={isFull} loading={booking} onClick={onBook}>
          {isFull ? t('speaking.full') : t('speaking.book')}
        </Button>
        {room.meet_link && (
          <a href={room.meet_link} target="_blank" rel="noreferrer">
            <Button variant="secondary">
              <span className="material-symbols-outlined">launch</span>
              {t('speaking.join')}
            </Button>
          </a>
        )}
      </div>
    </Card>
  );
}

function AITab() {
  const { t } = useTranslation();
  const [sessionOpen, setSessionOpen] = useState(false);
  const [topic, setTopic] = useState('');
  const sessions = useQuery({ queryKey: ['speaking', 'ai'], queryFn: speakingApi.listAISessions });

  return (
    <>
      <Card variant="subtle" className="mb-6">
        <CardTitle>{t('speaking.aiSessionTitle')}</CardTitle>
        <p className="mt-1 text-sm text-on-surface-variant">
          {t('speaking.aiSessionHint')}
        </p>
        <div className="mt-4 flex gap-2">
          <Input
            placeholder={t('speaking.aiTopicPlaceholder')}
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
          />
          <Button disabled={!topic.trim()} onClick={() => setSessionOpen(true)}>
            <span className="material-symbols-outlined">play_arrow</span>
            {t('speaking.start')}
          </Button>
        </div>
      </Card>

      {sessions.data && sessions.data.length > 0 && (
        <section>
          <h3 className="mb-3 font-display text-lg font-bold tracking-display">{t('speaking.pastSessions')}</h3>
          <ul className="space-y-2">
            {sessions.data.map((s) => (
              <li key={s.id}>
                <Card variant="subtle" className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-primary">forum</span>
                  <div className="flex-1">
                    <p className="font-semibold text-on-surface">{s.topic}</p>
                    <p className="text-xs text-on-surface-variant">
                      {new Date(s.created_at).toLocaleString('ru-RU')}
                    </p>
                  </div>
                  {s.cefr_level && <Pill variant="primary">{s.cefr_level}</Pill>}
                </Card>
              </li>
            ))}
          </ul>
        </section>
      )}

      {sessionOpen && (
        <AISessionModal
          topic={topic}
          onClose={() => {
            setSessionOpen(false);
            setTopic('');
          }}
        />
      )}
    </>
  );
}

function AISessionModal({ topic, onClose }: { topic: string; onClose: () => void }) {
  const { t } = useTranslation();
  type Turn = { role: 'student' | 'assistant'; content: string };
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [analysis, setAnalysis] = useState<AIAnalysis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Start the AI session once on mount. We guard with a ref so React 18
  // StrictMode's double-invoke (and any re-render) can't kick off a second
  // session — that previously created duplicate sessions for one modal.
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    speakingApi
      .startAI(topic)
      .then((s) => setSessionId(s.id))
      .catch((e) => setError(humanizeError(e)));
  }, [topic]);

  const sendText = async (raw: string) => {
    const message = raw.trim();
    if (!sessionId || !message) return;
    setTurns((t) => [...t, { role: 'student', content: message }]);
    setDraft('');
    setBusy(true);
    setError(null);
    try {
      const r = await speakingApi.sendAIMessage(sessionId, message);
      setTurns((t) => [...t, { role: 'assistant', content: r.response }]);
      // Auto-play the assistant reply as speech (voice out).
      try {
        const blob = await speakingApi.synthesize(r.response);
        playUrl(URL.createObjectURL(blob));
      } catch {
        // TTS failure is non-blocking
      }
    } catch (e) {
      // Roll back the optimistic student turn so they can retry.
      setTurns((t) => t.slice(0, -1));
      setDraft(message);
      setError(humanizeError(e));
    } finally {
      setBusy(false);
    }
  };

  const send = () => sendText(draft);

  // Voice in: record the student's speech, transcribe via Whisper
  // (/ai/transcribe), then send the text as their turn — making the trainer
  // fully conversational (speak in, hear the reply). Needs a secure context
  // (HTTPS) for microphone access.
  const toggleVoice = async () => {
    if (recording) {
      recRef.current?.stop();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError(t('speaking.micHttpsError'));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        setTranscribing(true);
        try {
          const { text } = await speakingApi.transcribe(blob);
          if (text?.trim()) await sendText(text);
          else setError(t('speaking.transcribeError'));
        } catch (e) {
          setError(humanizeError(e));
        } finally {
          setTranscribing(false);
        }
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
      setError(null);
    } catch {
      setError(t('speaking.micAccessError'));
    }
  };

  const finish = async () => {
    if (!sessionId) return;
    setBusy(true);
    setError(null);
    try {
      const a = await speakingApi.endAI(sessionId);
      setAnalysis(a);
    } catch (e) {
      setError(humanizeError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={analysis ? t('speaking.analysisTitle') : t('speaking.topicLabel', { topic })} size="lg">
      {analysis ? (
        <div className="space-y-3 text-sm">
          <Pill variant="primary">CEFR: {analysis.analysis.cefr_level}</Pill>
          <p><strong>{t('speaking.strengths')}:</strong> {analysis.analysis.strengths}</p>
          <p><strong>{t('speaking.improvements')}:</strong> {analysis.analysis.improvements}</p>
          <p><strong>{t('speaking.recommendations')}:</strong> {analysis.analysis.recommendations}</p>
          <Button onClick={onClose} fullWidth>{t('speaking.close')}</Button>
        </div>
      ) : error && !sessionId ? (
        <div className="space-y-4 text-center">
          <p className="text-sm text-error">{error}</p>
          <Button onClick={onClose} fullWidth>{t('speaking.close')}</Button>
        </div>
      ) : (
        <>
          <div className="mb-3 max-h-96 space-y-2 overflow-y-auto rounded-button bg-surface-container-low p-3">
            {turns.length === 0 ? (
              <p className="text-center text-sm text-on-surface-variant">
                {sessionId ? t('speaking.aiGreeting') : t('speaking.aiConnecting')}
              </p>
            ) : (
              turns.map((turn, i) => (
                <div
                  key={i}
                  className={cn(
                    'rounded-card p-2 text-sm',
                    turn.role === 'student'
                      ? 'ml-8 bg-primary-container text-on-primary-container'
                      : 'mr-8 bg-surface-container-lowest text-on-surface',
                  )}
                >
                  {turn.content}
                </div>
              ))
            )}
          </div>
          {error && sessionId && <p className="mb-2 text-sm text-error">{error}</p>}
          <div className="flex gap-2">
            <Button
              variant={recording ? 'primary' : 'ghost'}
              onClick={toggleVoice}
              disabled={busy || !sessionId || transcribing}
              title={recording ? t('speaking.stopRecord') : t('speaking.startVoice')}
              className={recording ? 'animate-pulse' : ''}
            >
              <span className="material-symbols-outlined">
                {transcribing ? 'progress_activity' : recording ? 'stop_circle' : 'mic'}
              </span>
            </Button>
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !busy && (e.preventDefault(), send())}
              placeholder={recording ? t('speaking.placeholderRecording') : transcribing ? t('speaking.placeholderTranscribing') : t('speaking.placeholderDefault')}
              className="flex-1 rounded-button bg-surface-container-low px-4 py-2 text-on-surface ring-1 ring-inset ring-outline-variant/30 focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={busy || !sessionId || recording || transcribing}
            />
            <Button disabled={!draft.trim() || busy || !sessionId} onClick={send}>
              <span className="material-symbols-outlined">send</span>
            </Button>
          </div>
          <div className="mt-3 flex justify-between">
            <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
            <Button variant="secondary" onClick={finish} loading={busy} disabled={turns.length < 4}>
              {t('speaking.finishAnalysis')}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

function StarRating({
  value,
  onChange,
  readOnly,
}: {
  value: number;
  onChange?: (v: number) => void;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={() => onChange?.(n)}
          className={cn(
            'material-symbols-outlined text-2xl leading-none transition',
            n <= value ? 'text-warning' : 'text-on-surface-variant/40',
            !readOnly && 'hover:scale-110 cursor-pointer',
          )}
          style={n <= value ? { fontVariationSettings: "'FILL' 1" } : undefined}
          aria-label={t('speaking.starsAriaLabel', { n })}
        >
          star
        </button>
      ))}
    </div>
  );
}

function HistoryEntryCard({ entry }: { entry: SpeakingHistoryEntry }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [rating, setRating] = useState(entry.rating?.rating ?? 0);
  const [comment, setComment] = useState(entry.rating?.comment ?? '');
  const [rateOpen, setRateOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const cancel = useMutation({
    mutationFn: () => speakingApi.cancelBooking(entry.room_id),
    onSuccess: () => {
      setConfirmCancel(false);
      qc.invalidateQueries({ queryKey: ['speaking', 'history'] });
      qc.invalidateQueries({ queryKey: ['speaking', 'rooms'] });
    },
  });

  const rate = useMutation({
    mutationFn: () => speakingApi.rateRoom(entry.room_id, rating, comment.trim() || undefined),
    onSuccess: () => {
      setRateOpen(false);
      qc.invalidateQueries({ queryKey: ['speaking', 'history'] });
    },
  });

  const statusLabels: Record<string, string> = {
    booked: t('speaking.statusBooked'),
    attended: t('speaking.statusAttended'),
    no_show: t('speaking.statusNoShow'),
    cancelled: t('speaking.statusCancelled'),
  };

  const when = entry.room?.scheduled_at ?? entry.booked_at;
  const canCancel = entry.status === 'booked'; // TZ 5.10: cancel at any time
  const canRate = entry.status === 'booked' || entry.status === 'attended';

  return (
    <Card variant="subtle" className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-on-surface">{entry.room?.title ?? t('speaking.sessionFallback')}</p>
          <p className="text-xs text-on-surface-variant">
            {new Date(when).toLocaleString('ru-RU', { dateStyle: 'medium', timeStyle: 'short' })}
          </p>
        </div>
        <Pill variant={entry.status === 'attended' ? 'primary' : entry.status === 'cancelled' ? 'warning' : 'neutral'}>
          {statusLabels[entry.status] ?? entry.status}
        </Pill>
      </div>

      {entry.rating && (
        <div className="flex items-center gap-2">
          <StarRating value={entry.rating.rating} readOnly />
          {entry.rating.comment && (
            <span className="text-xs text-on-surface-variant">{entry.rating.comment}</span>
          )}
        </div>
      )}

      {(canCancel || (canRate && !entry.rating) || rateOpen) && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {canCancel && (
            <Button variant="ghost" onClick={() => setConfirmCancel(true)}>
              {t('speaking.cancelBooking')}
            </Button>
          )}
          {canRate && !rateOpen && (
            <Button variant="secondary" onClick={() => setRateOpen(true)}>
              {entry.rating ? t('speaking.changeRating') : t('speaking.rate')}
            </Button>
          )}
        </div>
      )}

      {rateOpen && (
        <div className="space-y-2 rounded-button bg-surface-container-low p-3">
          <StarRating value={rating} onChange={setRating} />
          <Input
            placeholder={t('speaking.commentPlaceholder')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          {rate.isError && <p className="text-xs text-error">{humanizeError(rate.error)}</p>}
          <div className="flex gap-2">
            <Button disabled={rating < 1 || rating > 5} loading={rate.isPending} onClick={() => rate.mutate()}>
              {t('common.save')}
            </Button>
            <Button variant="ghost" onClick={() => setRateOpen(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}

      <Modal
        open={confirmCancel}
        onClose={() => !cancel.isPending && setConfirmCancel(false)}
        title={t('speaking.cancelConfirmTitle')}
        size="sm"
      >
        <p className="text-sm text-on-surface-variant">
          {t('speaking.cancelConfirmBody', { title: entry.room?.title ?? t('speaking.sessionFallback') })}
        </p>
        {cancel.isError && (
          <p className="mt-2 text-xs text-error">{humanizeError(cancel.error)}</p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setConfirmCancel(false)} disabled={cancel.isPending}>
            {t('common.back')}
          </Button>
          <Button variant="danger" loading={cancel.isPending} onClick={() => cancel.mutate()}>
            {t('speaking.cancelBooking')}
          </Button>
        </div>
      </Modal>
    </Card>
  );
}

function HistoryTab() {
  const { t } = useTranslation();
  const peer = useQuery({ queryKey: ['speaking', 'history'], queryFn: speakingApi.history });
  const ai = useQuery({ queryKey: ['speaking', 'ai-history'], queryFn: speakingApi.listAISessions });

  return (
    <div className="space-y-6">
      <section>
        <h3 className="mb-3 font-display text-lg font-bold tracking-display">{t('speaking.historyPeople')}</h3>
        {peer.data?.length ? (
          <ul className="space-y-2">
            {peer.data.map((b) => (
              <li key={b.id}>
                <HistoryEntryCard entry={b} />
              </li>
            ))}
          </ul>
        ) : <Card variant="subtle">{t('speaking.noSessions')}</Card>}
      </section>
      <section>
        <h3 className="mb-3 font-display text-lg font-bold tracking-display">{t('speaking.historyAI')}</h3>
        {ai.data?.length ? (
          <ul className="space-y-2">
            {ai.data.map((s) => <li key={s.id}><Card variant="subtle">{s.topic} — {new Date(s.created_at).toLocaleString('ru-RU')} {s.cefr_level && <Pill variant="primary">{s.cefr_level}</Pill>}</Card></li>)}
          </ul>
        ) : <Card variant="subtle">{t('speaking.noAISessions')}</Card>}
      </section>
    </div>
  );
}
